import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { ArrowLeft } from 'lucide-react';
import usePageTitle from '../../core/hooks/UsePageTitle';
import useIsMobile from '../../core/hooks/useIsMobile';
import useRevisaoSessao from '../../core/hooks/useRevisaoSessao';
import type { NotaRegistrada } from '../../core/hooks/revisaoSessao';
import { useProfileStore } from '../../core/store/useProfileStore';
import type { Rating, ReviewCard, ReviewSessionSummary, ReviewStats } from '../../sdk/@types/Revisao';
import QuestaoService from '../../sdk/services/QuestaoService';
import ReviewService from '../../sdk/services/ReviewService';
import { ADMIN, hasProfile } from '../auth/accessPolicy';
import DefaultLayout from '../layouts/default/Default.layout';
import ReviewCardFlip from '../components/ReviewCard/ReviewCardFlip';
import RatingBar from '../components/ReviewCard/RatingBar';
import { CardStack, type CardStackCard } from '../components/Revisao/CardStack';
import { ConclusaoSessao } from '../components/Revisao/ConclusaoSessao';
import { FilaVazia, LeituraFalhou } from '../components/Revisao/EstadosSessao';
import { rotuloMarcacaoDoCartao } from '../components/Revisao/oclusaoEstudo';
import { ProgressoSessao } from '../components/Revisao/ProgressoSessao';
import { EditorNaRevisao } from '../components/Revisao/EditorNaRevisao';
import { PilulaEditar } from '../components/Revisao/PilulaEditar';
import { ROTULO_MODO_ADMIN, useModoAdminSessao } from '../components/Revisao/modoAdmin';
import {
  ATALHO_EDITAR_SESSAO,
  teclaAbreEditor,
} from '../components/Revisao/editorNaRevisaoContrato';
import { SessaoFullscreen } from '../components/Revisao/SessaoFullscreen';
import { SessaoPainelLateral } from '../components/Revisao/SessaoPainelLateral';
import {
  entregaAnteciparAmanha,
  entregaRevisarErros,
  reforcoDasNotas,
} from '../components/Revisao/conclusaoLeitura';
import type { AgendaDia } from '../components/Revisao/home/revisaoHome';
import { proximosDaFila } from '../components/Revisao/painelLateral';
import {
  SessaoControles,
  DicaGesto,
  comandoDaTecla,
  focoEmCampoDeTexto,
} from '../components/Revisao/SessaoControles';

/**
 * A fase chega pronta de `useRevisaoSessao`. Progresso, pilha, notas e os
 * desfechos leem esse valor. Esta view não compara o índice com o tamanho
 * da fila — essa comparação é `faseDaSessao` (design.md, Decisão 4).
 */

interface EstudarLocationState {
  subjectId?: string;
  subjectIds?: string[];
  tag?: string;
  tags?: string[];
  limit?: number;
  ahead?: boolean;
  dueBefore?: string;
  cardIds?: string[];
  somenteDia?: string;
  agenda?: AgendaDia[];
}

/** O que esta sessão já sabe. O resto fica ausente — a conclusão mostra indisponível. */
function resumoDaSessao(
  fila: readonly ReviewCard[],
  notas: readonly NotaRegistrada[],
): ReviewSessionSummary {
  const ultima = new Map<string, Rating>();
  for (const nota of notas) {
    if (nota.desfeita) continue;
    ultima.set(nota.cardId, nota.rating);
  }
  const ratingCounts: Record<Rating, number> = { again: 0, hard: 0, good: 0, easy: 0 };
  const comNota = new Set<string>();
  for (const card of fila) {
    if (comNota.has(card.id)) continue;
    const rating = ultima.get(card.id);
    if (rating !== 'again' && rating !== 'hard' && rating !== 'good' && rating !== 'easy') continue;
    comNota.add(card.id);
    ratingCounts[rating] += 1;
  }
  return {
    cardsReviewed: comNota.size,
    ratingCounts,
    reinforceCards: reforcoDasNotas(fila, notas),
  };
}

/**
 * Uma leitura só, ou o resumo local. Não mistura os dois: cartões de um
 * lado e tempo do outro fariam a mesma tela discordar.
 *
 * A leitura entra inteira quando `cardsReviewed` é o número de cartões que
 * esta sessão avaliou — inclusive sequência, recorde e variação, que a tela
 * só formata (design.md, Decisão 6). Contagem diferente: a janela não pegou
 * estas respostas, e o resumo local deixa o que falta como indisponível.
 */
function resumoMostrado(
  api: ReviewSessionSummary | null,
  local: ReviewSessionSummary,
): ReviewSessionSummary {
  const localCards = local.cardsReviewed ?? 0;
  if (localCards === 0 || api?.cardsReviewed !== localCards || !api.ratingCounts || !api.reinforceCards) {
    return local;
  }
  return api;
}

function idsDosErros(summary: ReviewSessionSummary): string[] {
  const ids: string[] = [];
  for (const card of summary.reinforceCards ?? []) {
    if (card.id !== '') ids.push(card.id);
  }
  return ids;
}

function BotaoVoltar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-sm font-medium text-ink-secondary hover:text-ink"
    >
      <ArrowLeft size={16} /> Voltar
    </button>
  );
}

function ModoAdminInterruptor({
  ligado,
  onToggle,
}: {
  ligado: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={ROTULO_MODO_ADMIN}
      data-modo-admin-interruptor=""
      onClick={onToggle}
      className="inline-flex shrink-0 items-center gap-2 rounded-full px-1 py-1 outline-none focus-visible:ring-2 focus-visible:ring-admin/[0.42]"
    >
      <span className="text-xs font-medium text-ink-secondary">{ROTULO_MODO_ADMIN}</span>
      <span
        aria-hidden
        className={`relative h-5 w-8 shrink-0 rounded-full border border-edge transition-colors ${
          ligado ? 'bg-admin' : 'bg-surface'
        }`}
      >
        <span
          className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-all ${
            ligado ? 'right-0.5 bg-surface' : 'left-0.5 bg-ink-muted'
          }`}
        />
      </span>
    </button>
  );
}

export default function RevisaoEstudarView() {
  usePageTitle('Revisão — Estudar');
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as EstudarLocationState;

  const sessao = useRevisaoSessao({
    subjectId: state.subjectId,
    subjectIds: state.subjectIds,
    tag: state.tag,
    tags: state.tags,
    limit: state.limit ?? 20,
    ahead: state.ahead,
    dueBefore: state.dueBefore,
    cardIds: state.cardIds,
    somenteDia: state.somenteDia,
  });
  const { pular, registrar, atualizarCartao } = sessao;
  const estreito = useIsMobile();

  const profile = useProfileStore((state) => state.profile);
  const profileStatus = useProfileStore((state) => state.status);
  const loadProfile = useProfileStore((state) => state.load);
  const administra = hasProfile(profile, ADMIN);
  const { ligado: modoAdmin, alternar: alternarModoAdmin } = useModoAdminSessao();

  const [subjectNames, setSubjectNames] = useState<Record<string, string>>({});
  const [flipped, setFlipped] = useState(false);
  /** A marcação revelada abre as quatro notas sem virar o cartão e sem avaliar. */
  const [respostaAberta, setRespostaAberta] = useState(false);
  const [resumoApi, setResumoApi] = useState<ReviewSessionSummary | null>(null);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [cartaoEmEdicaoId, setCartaoEmEdicaoId] = useState<string | null>(null);
  const editorAberto = cartaoEmEdicaoId !== null;
  const colunaRef = useRef<HTMLDivElement>(null);

  const voltar = useCallback(() => navigate('/revisao'), [navigate]);

  // A rota é `gated(PAID)`, então `RequireProfile` já chama `load`. Esta
  // chamada cobre a tela se ela montar sem a guarda. `load` deduplica.
  useEffect(() => {
    if (profileStatus === 'idle') loadProfile();
  }, [profileStatus, loadProfile]);

  // No estreito a sessão sai do DefaultLayout, e com ela some quem punha
  // `html.dark`. A classe fica a do tema já escolhido — a conclusão usa os
  // mesmos tokens nos dois temas.
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const dark = saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.classList.toggle('dark', dark);
  }, []);

  useEffect(() => {
    QuestaoService.getSubjectsBySubscriber()
      .then((res: { id?: string; name?: string }[]) => {
        const map: Record<string, string> = {};
        (res || []).forEach((s) => {
          if (s?.id && s?.name) map[String(s.id)] = String(s.name);
        });
        setSubjectNames(map);
      })
      .catch(() => setSubjectNames({}));
  }, []);

  useEffect(() => {
    setFlipped(false);
    setRespostaAberta(false);
  }, [sessao.indice]);

  // Fecha ao trocar de cartão na fila. Trocar o objeto do cartão corrente
  // não muda o índice e não fecha.
  useEffect(() => {
    setCartaoEmEdicaoId(null);
  }, [sessao.indice]);

  // Janela da sessão que acabou: do carregamento da fila até pouco depois
  // da última resposta. O `at` do log é o relógio do servidor; o minuto a
  // mais cobre esse relógio adiantado em relação ao cliente. Mais longo que
  // a sessão, a igualdade de cartões abaixo recusa a leitura.
  useEffect(() => {
    if (sessao.fase !== 'concluida') {
      setResumoApi(null);
      return;
    }
    const inicio = sessao.iniciadaEm;
    if (inicio == null) return;
    if ((resumoDaSessao(sessao.fila, sessao.notas).cardsReviewed ?? 0) === 0) return;
    let ativo = true;
    ReviewService.getSessionSummary({
      from: new Date(inicio).toISOString(),
      to: new Date(Date.now() + 60_000).toISOString(),
    }).then(
      (summary) => {
        if (ativo) setResumoApi(summary);
      },
      () => {
        if (ativo) setResumoApi(null);
      },
    );
    return () => {
      ativo = false;
    };
  }, [sessao.fase, sessao.iniciadaEm, sessao.fila, sessao.notas]);

  // Uma leitura por fila montada — `iniciadaEm` só muda quando uma fila nova
  // termina de carregar (useRevisaoSessao.ts), nunca ao trocar de cartão nem
  // ao registrar uma nota. É `getStats`, nunca `getDue`/`getForecast`: a
  // razão de não materializar templates de admin mora em useDueCount.ts
  // (CLAUDE.md, "Due count (R16)") — não repetida aqui. Falhar deixa `stats`
  // em `null`; o painel já trata isso como indisponível, nunca zero.
  useEffect(() => {
    if (sessao.iniciadaEm == null) return;
    let ativo = true;
    ReviewService.getStats().then(
      (res) => {
        if (ativo) setStats(res);
      },
      () => {
        if (ativo) setStats(null);
      },
    );
    return () => {
      ativo = false;
    };
  }, [sessao.iniciadaEm]);

  const toggleFlip = useCallback(() => setFlipped((f) => !f), []);
  const estudando = sessao.fase === 'estudando';
  const cartao = sessao.cartao;
  const marcacao = estudando && cartao ? rotuloMarcacaoDoCartao(cartao) : null;

  useEffect(() => {
    if (sessao.fase !== 'estudando') return;
    const ativo = document.activeElement;
    if (focoEmCampoDeTexto(ativo)) return;
    colunaRef.current?.focus({ preventScroll: true });
  }, [sessao.fase, sessao.indice]);

  const aoTeclar = (event: KeyboardEvent<HTMLDivElement>) => {
    if (focoEmCampoDeTexto(event.target)) return;
    if (editorAberto) return;
    if (administra && estudando && teclaAbreEditor(event)) {
      event.preventDefault();
      if (cartao) setCartaoEmEdicaoId(cartao.id);
      return;
    }
    if (sessao.fase !== 'estudando') return;
    const comando = comandoDaTecla(event, flipped || respostaAberta);
    if (!comando) return;
    event.preventDefault();
    if (comando.tipo === 'virar') {
      setFlipped((f) => !f);
    } else if (comando.tipo === 'pular') {
      sessao.pular();
    } else {
      // The movement table lives on the rating button's click. A key that
      // called registrar directly would record the note and play nothing.
      const botao = colunaRef.current?.querySelector<HTMLButtonElement>(
        `button[data-rating="${comando.rating}"]`,
      );
      if (botao && !botao.disabled) {
        botao.click();
      } else if (!botao) {
        sessao.registrar(comando.rating);
      }
    }
  };

  const proximos = estudando ? proximosDaFila(sessao.fila, sessao.indice, subjectNames) : [];
  const atalhosExtra = administra ? [ATALHO_EDITAR_SESSAO] : undefined;
  const materias = Object.entries(subjectNames).map(([id, name]) => ({ id, name }));
  const fecharEditor = useCallback(() => {
    setCartaoEmEdicaoId(null);
  }, []);
  const aoSalvarEditor = useCallback(
    (card: ReviewCard) => {
      atualizarCartao(card);
      setCartaoEmEdicaoId(null);
    },
    [atualizarCartao],
  );
  // Métodos estáticos leem `this.Http`. O editor chama a função solta.
  const salvarCartao = useCallback(
    (id: string, body: Parameters<typeof ReviewService.updateCard>[1]) =>
      ReviewService.updateCard(id, body),
    [],
  );
  const carregarHistoricoCartao = useCallback(
    (id: string) => ReviewService.getHistory(id),
    [],
  );

  const cartaoEmEdicao = cartaoEmEdicaoId
    ? sessao.fila.find((c) => c.id === cartaoEmEdicaoId)
    : undefined;

  const editor =
    administra && cartaoEmEdicao ? (
      <EditorNaRevisao
        card={cartaoEmEdicao}
        estreito={estreito}
        posicao={sessao.posicao}
        total={sessao.total}
        materias={materias}
        salvar={salvarCartao}
        carregarHistorico={carregarHistoricoCartao}
        onCancelar={fecharEditor}
        onSalvo={aoSalvarEditor}
      />
    ) : null;

  const pilha: CardStackCard[] =
    estudando && cartao
      ? sessao.fila.slice(sessao.indice).map((card, offset) => ({
          id: card.id,
          content:
            offset === 0 ? (
              <div className="w-[min(440px,calc(100vw-2rem))] max-w-full">
                <ReviewCardFlip
                  card={cartao}
                  flipped={flipped}
                  onToggleFlip={toggleFlip}
                  onRevelar={() => setRespostaAberta(true)}
                  onSwipe={editorAberto ? undefined : () => pular()}
                  subjectName={subjectNames[cartao.subjectId] ?? cartao.question?.subject?.name}
                  editSlot={administra && modoAdmin ? <PilulaEditar onClick={() => setCartaoEmEdicaoId(cartao.id)} /> : undefined}
                  ratingSlot={
                    !estreito && (flipped || respostaAberta) ? (
                      <RatingBar
                        submitting={sessao.registrando}
                        onRate={registrar}
                        previews={cartao.previews}
                      />
                    ) : undefined
                  }
                  className="w-full"
                />
              </div>
            ) : null,
        }))
      : [];

  const resumoConclusao =
    sessao.fase === 'concluida'
      ? resumoMostrado(resumoApi, resumoDaSessao(sessao.fila, sessao.notas))
      : null;

  const conclusao = resumoConclusao ? (
    <div data-sessao-estado="concluida" className="contents">
      <ConclusaoSessao
        summary={resumoConclusao}
        dias={state.agenda ?? []}
        assuntos={subjectNames}
        onVoltar={() => navigate('/revisao')}
        onAntecipar={() => {
          const entrega = entregaAnteciparAmanha(state.agenda ?? []);
          if (!entrega) return;
          navigate('/revisao/estudar', { state: entrega });
        }}
        onRevisarErros={() => {
          const ids = idsDosErros(resumoConclusao);
          if (ids.length === 0) return;
          navigate('/revisao/estudar', {
            state: entregaRevisarErros(ids, state.agenda),
          });
        }}
      />
    </div>
  ) : null;

  // The conclusion is its own screen: header, scrolling body, thumb-reach
  // actions. SessaoFullscreen is the study shell and would nest a second one.
  if (estreito && conclusao) return conclusao;

  if (estreito) {
    return (
      <>
        <SessaoFullscreen
          className={editorAberto ? 'pointer-events-none' : undefined}
          assunto={
            estudando && cartao
              ? (subjectNames[cartao.subjectId] ?? cartao.question?.subject?.name ?? '')
              : ''
          }
          posicao={sessao.posicao}
          total={sessao.total}
          estudando={estudando}
          marcacao={marcacao}
          onFechar={voltar}
          acaoDireita={
            administra ? (
              <ModoAdminInterruptor ligado={modoAdmin} onToggle={alternarModoAdmin} />
            ) : undefined
          }
          onKeyDown={aoTeclar}
          focoRef={colunaRef}
          controles={
            estudando && cartao ? (
              flipped || respostaAberta ? (
                <RatingBar
                  className="w-full"
                  submitting={sessao.registrando}
                  onRate={registrar}
                  previews={cartao.previews}
                />
              ) : (
                <div className="flex flex-col gap-3">
                  <DicaGesto />
                  <SessaoControles
                    podeDesfazer={sessao.podeDesfazer}
                    onDesfazer={sessao.desfazer}
                    onVirar={() => setFlipped(true)}
                    onPular={pular}
                    ocupado={sessao.registrando}
                  />
                </div>
              )
            ) : undefined
          }
        >
          <div className="flex flex-col items-center px-4 py-4">
            {sessao.fase === 'carregando' && (
              <div className="flex justify-center py-20" data-sessao-estado="carregando">
                <Spin size="large" tip="Carregando revisão..." />
              </div>
            )}

            {sessao.fase === 'vazia' && <FilaVazia onVoltar={voltar} />}

            {sessao.fase === 'falhou' && <LeituraFalhou onTentarDeNovo={sessao.tentarDeNovo} />}

            {estudando && cartao && pilha.length > 0 && (
              <div className="flex w-full max-w-[440px] flex-col items-center">
                <CardStack cards={pilha} className="w-full" />
              </div>
            )}
          </div>
        </SessaoFullscreen>
        {editor}
      </>
    );
  }

  return (
    <DefaultLayout hideFilter>
      <div
        ref={colunaRef}
        tabIndex={0}
        onKeyDown={aoTeclar}
        className="mx-auto flex w-full max-w-6xl flex-col bg-canvas px-4 py-4 outline-none focus-visible:ring-2 focus-visible:ring-select/[0.42] sm:py-6 md:flex-row md:items-start md:gap-6"
      >
        <div className={`flex min-w-0 flex-1 flex-col ${editorAberto ? 'pointer-events-none' : ''}`}>
          {estudando ? (
            <div className="sticky top-0 z-20 -mx-4 mb-4 bg-canvas px-4 pb-3 pt-1">
              <div className="mb-3 flex items-center justify-between gap-3">
                <BotaoVoltar onClick={voltar} />
                {administra && (
                  <ModoAdminInterruptor ligado={modoAdmin} onToggle={alternarModoAdmin} />
                )}
              </div>
              <ProgressoSessao
                posicao={sessao.posicao}
                total={sessao.total}
                faltam={sessao.faltam}
                contagens={sessao.contagens}
                marcacao={marcacao}
              />
            </div>
          ) : (
            <div className="mb-4 flex items-center justify-between gap-3">
              <BotaoVoltar onClick={voltar} />
              {administra && (
                <ModoAdminInterruptor ligado={modoAdmin} onToggle={alternarModoAdmin} />
              )}
            </div>
          )}

          <div className="flex flex-1 flex-col items-center">
            {sessao.fase === 'carregando' && (
              <div className="flex justify-center py-20" data-sessao-estado="carregando">
                <Spin size="large" tip="Carregando revisão..." />
              </div>
            )}

            {sessao.fase === 'vazia' && <FilaVazia onVoltar={voltar} />}

            {sessao.fase === 'falhou' && <LeituraFalhou onTentarDeNovo={sessao.tentarDeNovo} />}

            {conclusao}

            {estudando && cartao && pilha.length > 0 && (
              <div className="flex w-full max-w-[440px] flex-col items-center">
                <CardStack cards={pilha} className="w-full" />
                <div className="mt-4 w-full">
                  <DicaGesto />
                </div>
                {!(flipped || respostaAberta) && (
                  <div className="mt-6 w-full">
                    {/* Desfazer só monta quando `podeDesfazer`. Sem a rota, o hook
                        deixa isso falso — a tela não recua o índice no lugar (Decisão 5). */}
                    <SessaoControles
                      podeDesfazer={sessao.podeDesfazer}
                      onDesfazer={sessao.desfazer}
                      onVirar={() => setFlipped(true)}
                      onPular={pular}
                      ocupado={sessao.registrando}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {editor ? (
          <div className="w-full shrink-0 md:sticky md:top-4 md:max-h-[calc(100vh-2rem)] md:w-[420px]">
            {editor}
          </div>
        ) : estudando ? (
          <div className="w-72 shrink-0">
            {/* O painel já é o <aside>. Acerto, sequência, variação e recorde
                vêm da leitura de GET /review/stats acima, uma vez por sessão
                — indisponível enquanto ela não resolve ou se falhar, nunca
                um zero inventado (painelLateral.ts). */}
            <SessaoPainelLateral
              posicao={sessao.posicao}
              total={sessao.total}
              contagens={sessao.contagens}
              acerto={stats?.retention ?? null}
              sequencia={stats?.streak ?? null}
              acertoDelta={stats?.retentionDeltaVsYesterday}
              sequenciaRecorde={stats?.bestStreak}
              proximos={proximos}
              atalhosExtra={atalhosExtra}
              onEditarRapido={
                administra && modoAdmin ? (id: string) => setCartaoEmEdicaoId(id) : undefined
              }
            />
          </div>
        ) : null}
      </div>
    </DefaultLayout>
  );
}
